/**
 * <add-to-cart-form> — Web Component that progressively enhances the
 * product page's add-to-cart form. Without JS, the form submits natively
 * to /cart/add and the browser navigates to /cart on success — fully
 * functional. With JS, this component intercepts the submit, POSTs to
 * the form's action (routes.cart_add_url, i.e. /cart/add) with an
 * Accept: application/javascript header so Shopify replies with JSON,
 * surfaces success or error in the aria-live status region, and redirects
 * to /cart (or /checkout for the Buy Now button) on success.
 *
 * Why we still redirect after success: a cart drawer is a separate
 * focus-trapped component out of scope for v1. Redirecting matches the
 * no-JS behavior so users get the same destination either way.
 *
 * Markup contract:
 *   <add-to-cart-form>
 *     <form data-product-form action="/cart/add" method="post" ...>
 *       ...
 *       <div data-quantity-stepper>
 *         <button data-quantity-decrement>−</button>
 *         <input data-quantity-input type="number" ... />
 *         <button data-quantity-increment>+</button>
 *       </div>
 *       <button type="submit" name="add">Add to cart</button>
 *       <button type="submit" name="checkout">Buy</button>
 *     </form>
 *     <div data-cart-status role="status" aria-live="polite"></div>
 *   </add-to-cart-form>
 */

class AddToCartForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-product-form]');
    this.status = this.querySelector('[data-cart-status]');
    this.quantityInput = this.querySelector('[data-quantity-input]');
    this.decrement = this.querySelector('[data-quantity-decrement]');
    this.increment = this.querySelector('[data-quantity-increment]');

    if (!this.form) return;

    this.form.addEventListener('submit', this.onSubmit);
    if (this.decrement) this.decrement.addEventListener('click', this.onDecrement);
    if (this.increment) this.increment.addEventListener('click', this.onIncrement);

    this.lastSubmitter = null;
    this.form.addEventListener('click', this.captureSubmitter, true);

    this.pressureRadios = this.form.querySelectorAll('input[name="properties[System Pressure]"]');
    if (this.pressureRadios.length > 0) {
      const anyChecked = Array.from(this.pressureRadios).some((r) => r.checked);
      if (!anyChecked) {
        this.setPressureGate(true);
      }
      this.pressureFieldset = this.form.querySelector('[data-pressure-fieldset]');
      if (this.pressureFieldset) {
        this.pressureFieldset.addEventListener('change', this.onPressureChange);
      }
    }
  }

  disconnectedCallback() {
    if (!this.form) return;
    this.form.removeEventListener('submit', this.onSubmit);
    this.form.removeEventListener('click', this.captureSubmitter, true);
    if (this.decrement) this.decrement.removeEventListener('click', this.onDecrement);
    if (this.increment) this.increment.removeEventListener('click', this.onIncrement);
    if (this.pressureFieldset) {
      this.pressureFieldset.removeEventListener('change', this.onPressureChange);
    }
  }

  captureSubmitter = (event) => {
    if (event.target.matches('button[type="submit"]')) {
      this.lastSubmitter = event.target;
    }
  };

  onPressureChange = () => {
    const anyChecked = Array.from(this.pressureRadios).some((r) => r.checked);
    if (anyChecked) {
      this.setPressureGate(false);
    }
  };

  setPressureGate(gated) {
    const buttons = this.form.querySelectorAll('button[type="submit"]');
    buttons.forEach((button) => {
      if (!button.hasAttribute('data-sold-out')) {
        button.disabled = gated;
      }
    });
  }

  onDecrement = () => {
    if (!this.quantityInput) return;
    const current = Number(this.quantityInput.value) || 1;
    const min = Number(this.quantityInput.min) || 1;
    if (current > min) {
      this.quantityInput.value = String(current - 1);
    }
  };

  onIncrement = () => {
    if (!this.quantityInput) return;
    const current = Number(this.quantityInput.value) || 1;
    this.quantityInput.value = String(current + 1);
  };

  onSubmit = async (event) => {
    event.preventDefault();
    const submitter = event.submitter || this.lastSubmitter;
    const isCheckout = submitter && submitter.name === 'checkout';
    this.setStatus('');
    this.setSubmitting(true);

    try {
      const formData = new FormData(this.form);
      const response = await fetch(this.form.action, {
        method: 'POST',
        headers: { Accept: 'application/javascript' },
        body: formData,
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        const message = (body && (body.description || body.message)) || this.errorMessage();
        this.setStatus(message, 'error');
        this.setSubmitting(false);
        return;
      }

      this.setStatus(this.successMessage(), 'success');
      window.location.href = isCheckout ? '/checkout' : '/cart';
    } catch {
      this.setStatus(this.errorMessage(), 'error');
      this.setSubmitting(false);
    }
  };

  setSubmitting(isSubmitting) {
    const buttons = this.form.querySelectorAll('button[type="submit"]');
    buttons.forEach((button) => {
      // Never re-enable a server-rendered sold-out button: it must stay
      // disabled regardless of submit state (matches setPressureGate).
      if (button.hasAttribute('data-sold-out')) return;
      button.disabled = isSubmitting;
    });
  }

  setStatus(message, tone) {
    if (!this.status) return;
    this.status.textContent = message;
    this.status.classList.remove('text-accent', 'text-ink-strong');
    if (tone === 'error') {
      this.status.classList.add('text-accent');
    } else if (tone === 'success') {
      this.status.classList.add('text-ink-strong');
    }
  }

  successMessage() {
    return this.dataset.successMessage || 'Added to cart';
  }

  errorMessage() {
    return this.dataset.errorMessage || 'Could not add to cart. Please try again.';
  }
}

if (!customElements.get('add-to-cart-form')) {
  customElements.define('add-to-cart-form', AddToCartForm);
}
